/**
 * Admin Panel Menu Configuration
 * Centralized menu structure for easier maintenance
 */
import { Home, Car, 
  BarChart3, Users, Lock, ClipboardList, Package, Shield, Store,
  Truck, MapPin, FileText, Settings, Bell, QrCode, Mail, Sliders, Droplet,
  ShieldCheck, ShieldAlert, ShoppingCart, Flag, Scan, DollarSign,
  Calculator, Layers, FolderTree, Gavel, MapPinned, Headphones, Coins, Camera
} from 'lucide-react';

// Menu Categories
export const menuCategories = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    items: [
      { 
        id: 'overview', 
        label: 'Overview', 
        icon: BarChart3, 
        path: '/admin',
        description: 'System stats and quick actions',
        adminOnly: true
      },
      {
        id: 'analytics',
        label: 'Analytics',
        icon: BarChart3,
        path: '/admin/analytics',
        description: 'Business intelligence & metrics',
        adminOnly: true
      }
    ]
  },
  {
    id: 'users',
    label: 'User Management',
    items: [
      { 
        id: 'users',
        label: 'All Users',
        icon: Users,
        path: '/admin/users',
        description: 'Add, suspend, disable users',
        adminOnly: true
      },
      { 
        id: 'staff', 
        label: 'Staff', 
        icon: Users, 
        path: '/admin/staff',
        description: 'Manage staff and functions',
        adminOnly: true
      },
      {
        id: 'password-resets',
        label: 'Password Resets',
        icon: Lock,
        path: '/admin/password-resets',
        description: 'Approve reset requests',
        adminOnly: true
      },
      {
        id: 'applications',
        label: 'Applications',
        icon: ClipboardList,
        path: '/admin/applications',
        description: 'Driver, business & PUDO apps',
        adminOnly: true
      },
      {
        id: 'support',
        label: 'Customer Support',
        icon: Headphones,
        path: '/admin/support',
        description: 'Support tickets & queries',
        function: 'support',
        
      }
    ]
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'assisted-booking',
        label: 'Assisted Booking',
        icon: Shield,
        path: '/admin/assisted-booking',
        description: 'Book on behalf of customers',
        adminOnly: true,
        isNew: true,
        newUntil: '2026-06-30'
      },
      {
        id: 'fulfillment',
        label: 'Fulfillment',
        icon: Package,
        path: '/admin/fulfillment',
        description: 'Orders and delivery status',
        adminOnly: true
      },
      {
        id: 'unified-orders',
        label: 'Unified Orders',
        icon: Layers,
        path: '/admin/unified-orders',
        description: 'Order lifecycle tracking',
        adminOnly: true,
        isNew: true,
        newUntil: '2026-06-08'
      },
      { 
        id: 'drivers',  
        label: 'Drivers', 
        icon: Truck, 
        path: '/admin/drivers',
        description: 'View and manage drivers',
        function: 'drivers'
      },
      { 
        id: 'businesses', 
        label: 'Businesses/PUDO', 
        icon: MapPin, 
        path: '/admin/businesses',
        description: 'Businesses and PUDO points',
        function: 'businesses'
      }
    ]
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    items: [
      { 
        id: 'products', 
        label: 'Products', 
        icon: Package, 
        path: '/admin/products',
        description: 'Products and inventory',
        function: 'inventory'
      },
      { 
        id: 'featured-stores', 
        label: 'Featured Stores', 
        icon: Store, 
        path: '/admin/featured-stores',
        description: 'Homepage store showcase',
        adminOnly: true
      },
      {
        id: 'categories',
        label: 'Categories',
        icon: FolderTree,
        path: '/admin/categories',
        description: 'Product categories',
        adminOnly: true
      },
      {
        id: 'abandoned-carts',
        label: 'Abandoned Carts',
        icon: ShoppingCart,
        path: '/admin/abandoned-carts',
        description: 'Cart recovery',
        adminOnly: true
      },
      {
        id: 'classifieds',
        label: 'Classifieds',
        icon: Home,
        path: '/admin/classifieds',
        description: 'Property & Vehicle listings',
        adminOnly: true,
        isNew: true,
        newUntil: '2026-06-09'
      },
    ]
  },
  {
    id: 'compliance',
    label: 'Compliance & Safety',
    items: [
      {
        id: 'security-audit',
        label: 'Security Audit',
        icon: Shield,
        path: '/admin/security-audit',
        description: 'Login attempts & password changes',
        adminOnly: true,
        isNew: true,
        newUntil: '2026-08-15'
      },
      {
        id: 'listing-reports',
        label: 'Listing Reports',
        icon: Flag,
        path: '/admin/listing-reports',
        description: 'Reports, strikes & compliance',
        adminOnly: true
      },
      { 
        id: 'compliance-settings', 
        label: 'Package Compliance', 
        icon: ShieldCheck, 
        path: '/admin/compliance-settings',
        description: 'Prohibited items & rules',
        adminOnly: true
      },
      { 
        id: 'safety-reviews', 
        label: 'Safety Reviews', 
        icon: ShieldAlert, 
        path: '/admin/safety-reviews',
        description: 'Driver reports queue',
        adminOnly: true
      },
      { 
        id: 'seller-compliance', 
        label: 'Seller Rules', 
        icon: Store, 
        path: '/admin/seller-compliance',
        description: 'Marketplace listing rules',
        adminOnly: true
      },
      {
        id: "disputes",
        label: "Disputes",
        icon: Shield,
        path: "/admin/disputes",
        description: "Disputes and account freezes",
        adminOnly: true
      },
      {
        id: "photo-gallery",
        label: "Photo Gallery",
        icon: Camera,
        path: "/admin/photos",
        description: "View package photos for disputes",
        adminOnly: true
      },
      {
        id: 'bidder-management',
        label: 'Bidder Management',
        icon: Gavel,
        path: '/admin/bidder-management',
        description: 'Strikes and bans for bidders',
        adminOnly: true
      }
    ]
  },
  {
    id: 'finance',
    label: 'Finance',
    items: [
      { 
        id: 'seller-billing', 
        label: 'Seller Billing', 
        icon: DollarSign, 
        path: '/admin/seller-billing',
        description: 'Fees & invoicing',
        adminOnly: true
      },
      {
        id: "token-management",
        label: "Token Management",
        icon: Coins,
        path: "/admin/tokens",
        description: "Blocks, promotions, rewards",
        adminOnly: true,
        isNew: true,
        newUntil: "2026-06-09"
      },
      { 
        id: 'pricing', 
        label: 'Pricing & Promos', 
        icon: Calculator, 
        path: '/admin/pricing',
        description: 'Pricing and discounts',
        function: 'pricing'
      },
      {
        id: 'shipping-calc',
        label: 'Shipping Calculator',
        icon: Calculator,
        path: '/admin/shipping',
        description: 'Delivery pricing rules',
        adminOnly: true
      },
      {
        id: 'shipping-pricing',
        label: 'Unified Pricing',
        icon: DollarSign,
        path: '/admin/shipping-pricing',
        description: 'PUDO & Address pricing config',
        adminOnly: true
      }
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    items: [
      { 
        id: 'platform-settings', 
        label: 'Platform', 
        icon: Settings, 
        path: '/admin/platform-settings',
        description: 'Fee splits, tips & commission',
        adminOnly: true
      },
      { 
        id: 'site-settings', 
        label: 'Site Settings', 
        icon: Sliders, 
        path: '/admin/site-settings',
        description: 'Theme & display',
        adminOnly: true
      },
      {
        id: 'site-config',
        label: 'Site Config',
        icon: Settings,
        path: '/admin/site-config',
        description: 'Contact, Social & Legal docs',
        adminOnly: true,
        isNew: true
      },
      { 
        id: 'watermark-settings', 
        label: 'Watermark', 
        icon: Droplet, 
        path: '/admin/watermark',
        description: 'Product image watermarks',
        adminOnly: true
      },
      { 
        id: 'email-templates', 
        label: 'Email Templates', 
        icon: Mail, 
        path: '/admin/email-templates',
        description: 'Email customization',
        adminOnly: true
      },
      { 
        id: 'notifications', 
        label: 'Push Notifications', 
        icon: Bell, 
        path: '/admin/notifications',
        description: 'Browser notifications',
        adminOnly: true
      },
      { 
        id: 'audit-logs', 
        label: 'Audit Logs', 
        icon: FileText, 
        path: '/admin/audit-logs',
        description: 'System activity logs',
        function: 'audit'
      }
    ]
  }
];

// Flatten menu for searching
export const getAllMenuItems = () => {
  return menuCategories.flatMap(cat => cat.items);
};

// Filter by user access
export const getAccessibleMenu = (isAdmin, staffFunctions = []) => {
  return menuCategories.map(category => ({
    ...category,
    items: category.items.filter(item => {
      if (item.adminOnly) return isAdmin;
      if (item.function) return isAdmin || staffFunctions.includes(item.function);
      return true;
    })
  })).filter(cat => cat.items.length > 0);
};

export default menuCategories;
